import { queryOps, Query } from "system/query/Query";
import { NamedColors } from "theme/infegyColor";
import { QueryBranch } from "./QueryBranch";
import { QuerySpan } from "./QuerySpan";
import { QuerySegment } from "./QuerySegment";
import { SxProps } from "@mui/system";
import { CSS, Transform } from '@dnd-kit/utilities';
import { Theme } from "@emotion/react";
import { QueryLeafNode } from "./QueryLeafNode";
import APIRequest from "system/APIRequest";
import { spanSide } from "components/queries/queryBuilder/common/QuerySegmentContainer";

export const styles = {
    boxBorderRadius: '6px',
    boxPadding: '10px 6px 6px'
}

export const defaultQueryFieldTypeOrder = [
  // Social specific
  "stats",
  "text",
  "date / time",
  "source",
  "social metrics",
  "infegy IQ",
  "geography",
  "demographics",
  "other",

  // General
  "text",
  "timestamp",
  "integer",
  "decimal",
  "keyword",
  "keywords",
  "uid",
  "latitude_longitude",
  "boolean",
];

export const allowedFieldTypesByOpType = {
  match: [ "keyword", "keywords", "uid", "latitude_longitude", "timestamp", "integer", "decimal", "boolean" ],
  range: [ "timestamp", "integer", "decimal" ],
  boolean: [ "text", "keyword", "keywords" ],
  span: [ "text" ],
  compare: [ "timestamp", "integer", "decimal" ],
  contains: [ "text", "keyword", "keywords", "timestamp", "integer", "decimal", "boolean", "uid"]
};

export function getAllowedLeafOpByFieldType(fieldType: string): string[] {
  const allowedOps:string[] = [];
  if (allowedFieldTypesByOpType.match.includes(fieldType)) {
    allowedOps.push(queryOps.equals);
  }
  if (allowedFieldTypesByOpType.range.includes(fieldType)) {
    allowedOps.push(queryOps.range);
  }
  if (allowedFieldTypesByOpType.boolean.includes(fieldType)) {
    allowedOps.push(queryOps.boolean);
  }
  if (allowedFieldTypesByOpType.compare.includes(fieldType)) {
    allowedOps.push(queryOps.lessThan, queryOps.lessThanOrEqual, queryOps.greaterThan, queryOps.greaterThanOrEqual);
  }
  if (allowedFieldTypesByOpType.contains.includes(fieldType)) {
    allowedOps.push(queryOps.contains);
  }
  return allowedOps;
}

export function getAllowedFieldTypesByOp(op: queryOps): string[] {
  if (queryOpIsCompare(op)) {
    return allowedFieldTypesByOpType.compare;
  } else if (op === queryOps.range) {
    return allowedFieldTypesByOpType.range;
  } else if (op === queryOps.boolean) {
    return allowedFieldTypesByOpType.boolean;
  } else if (op === queryOps.contains) {
    return allowedFieldTypesByOpType.contains;
  } else if (op === queryOps.equals) {
    return allowedFieldTypesByOpType.match;
  } else if (op === queryOps.span) {
    return allowedFieldTypesByOpType.span;
  }
  return [];
}

export function makeBoxStyles(backgroundColor: string, borderColor: string, borderColorTop: string,
  isActiveDropTarget?:boolean, transform?:Transform | null, isAddingActive?: boolean, isDragging?: boolean): SxProps<Theme> {
  const borderStyle:string = isActiveDropTarget ? 'dashed' : 'solid';

  const positioning = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    zIndex: isDragging ? 9000 : 0,
    opacity: (isAddingActive && !isDragging) ? 0.5 : 1,
    '&:hover': {
      zIndex: 9000,
    }
  };

  const styling = {
    position:'relative',
    padding: "3px", mL:0,
    marginTop: "0px",
    borderBottom: `1px ${borderStyle} ${borderColor}`,
    borderLeft: `1px ${borderStyle} ${borderColor}`,
    borderTop: `1px ${borderStyle} ${borderColorTop || borderColor}`,
    borderRight: `1px ${borderStyle} ${borderColorTop || borderColor}`,
    boxShadow: `-1px 1px 2px 0px ${borderColor}`,
    borderRadius: styles.boxBorderRadius,
    backgroundColor: `${backgroundColor}`,
    marginRight:'auto',
  };

  return {...styling, ...positioning};
}

export function colorsAndLabelsByOp(i18n: any, namedColors:NamedColors, op?:queryOps | string) {
  if (!op) {
    return {
      borderColor: namedColors.queryUndefinedBorder,
      borderColorTop: namedColors.queryUndefinedBorderTop,
      backgroundColor: namedColors.queryUndefined,
      label: i18n.t("undefined")
    };
  }
  if (op === queryOps.and) {
    return {
      borderColor: namedColors.queryAndBorder,
      borderColorTop: namedColors.queryAndBorderTop,
      backgroundColor: namedColors.queryAnd,
      label: i18n.t("query-form.and"), shortLabel: i18n.t("query-form.and-short")
    };
  } else if (op === queryOps.or) {
    return {
      borderColor: namedColors.queryOrBorder,
      borderColorTop: namedColors.queryOrBorderTop,
      backgroundColor: namedColors.queryOr,
      label: i18n.t("query-form.or"), shortLabel: i18n.t("query-form.or-short")
    };
  } else if (op === queryOps.not) {
    return {
      borderColor: namedColors.queryNotBorder,
      borderColorTop: namedColors.queryNotBorderTop,
      backgroundColor: namedColors.queryNot,
      label: i18n.t("query-form.not"), shortLabel: i18n.t("query-form.not-short")
    };
  } else if (op === queryOps.contains) {
    return {
      borderColor: namedColors.queryContainsBorder,
      borderColorTop: namedColors.queryContainsBorderTop,
      backgroundColor: namedColors.queryContains,
      label: i18n.t("query-form.contains")
    };
  } else if (op === queryOps.boolean) {
    return {
      borderColor: namedColors.queryBooleanBorder,
      borderColorTop: namedColors.queryBooleanBorderTop,
      backgroundColor: namedColors.queryBoolean,
      label: i18n.t("query-form.boolean")
    };
  } else if (op === queryOps.span) {
    return {
      borderColor: namedColors.querySpanBorder,
      borderColorTop: namedColors.querySpanBorderTop,
      backgroundColor: namedColors.querySpan,
      label: i18n.t("query-form.span")
    };
  } else if (op === queryOps.equals) {
    return {
      borderColor: namedColors.queryMatchBorder,
      borderColorTop: namedColors.queryMatchBorderTop,
      backgroundColor: namedColors.queryMatch,
      label: i18n.t("query-form.equals")
    };
  } else if (op === queryOps.range) {
    return {
      borderColor: namedColors.queryRangeBorder,
      borderColorTop: namedColors.queryRangeBorderTop,
      backgroundColor: namedColors.queryRange,
      label: i18n.t("query-form.range")
    };
  } else if (queryOpIsCompare(op as queryOps)) {
    let label = i18n.t("query-form.compare");
    if (op === queryOps.lessThan) {
      label = i18n.t("query-form.less-than");
    } else if (op === queryOps.lessThanOrEqual) {
      label = i18n.t("query-form.less-than-or-equal");
    } else if (op === queryOps.greaterThan) {
      label = i18n.t("query-form.greater-than");
    } else if (op === queryOps.greaterThanOrEqual) {
      label = i18n.t("query-form.greater-than-or-equal");
    } else if (op === queryOps.equals) {
      label = i18n.t("query-form.equals");
    }
    return {
      borderColor: namedColors.queryCompareBorder,
      borderColorTop: namedColors.queryCompareBorderTop,
      backgroundColor: namedColors.queryCompare,
      label: label
    };
  }
  return {
    borderColor: namedColors.queryAndBorder,
    borderColorTop: namedColors.queryAndBorderTop,
    backgroundColor: namedColors.queryAnd,
    label: i18n.t("query-form.and")
  };
}

export function getAllLeafOpInfo(i18n: any, namedColors:NamedColors) {
  return [
    { op: queryOps.contains, ...colorsAndLabelsByOp(i18n, namedColors, queryOps.contains) },
    { op: queryOps.range, ...colorsAndLabelsByOp(i18n, namedColors, queryOps.range) },
    { op: queryOps.boolean, ...colorsAndLabelsByOp(i18n, namedColors, queryOps.boolean) },
    { op: queryOps.lessThan, ...colorsAndLabelsByOp(i18n, namedColors, queryOps.lessThan) },
    { op: queryOps.lessThanOrEqual, ...colorsAndLabelsByOp(i18n, namedColors, queryOps.lessThanOrEqual) },
    { op: queryOps.greaterThan, ...colorsAndLabelsByOp(i18n, namedColors, queryOps.greaterThan) },
    { op: queryOps.greaterThanOrEqual, ...colorsAndLabelsByOp(i18n, namedColors, queryOps.greaterThanOrEqual) },
    { op: queryOps.equals, ...colorsAndLabelsByOp(i18n, namedColors, queryOps.equals) }
  ];
}

export function findQuerySegmentByPath(querySegment:any, path:string, parentElement?: any, parentPath?: string, callback?: Function, inSpan?:boolean, spanSide?:string): [QuerySegment | undefined, any, boolean | undefined, string | undefined] {
  if (!querySegment || !querySegment.op || !path) {
    return [undefined, parentElement, inSpan, spanSide];
  }
  const pathParts:string[] = path.split("."),
      currentPath:string = pathParts[0];

  if (currentPath === "values" && querySegment?.values?.length) {
    const index = pathParts[1],
        segment = querySegment?.values?.[index],
        nextPath = pathParts.slice(2).join(".");
    if (!nextPath) {
      return [segment, querySegment, inSpan, spanSide];
    } else {
      return findQuerySegmentByPath(segment, nextPath, querySegment, `${parentPath}.values.${index}`, callback, inSpan, spanSide);
    }
  } else if (currentPath === "left" || currentPath === "right") {
    const segment = querySegment?.[currentPath],
        nextPath = pathParts.slice(1).join(".");
    if (!nextPath) {
      return [segment, querySegment, inSpan, spanSide];
    } else {
      return findQuerySegmentByPath(segment, nextPath, querySegment, `${parentPath}.${nextPath}`, callback, inSpan, spanSide);
    }
  } else if (querySegment) {
    return [querySegment, parentElement, inSpan, spanSide];
  }

  return [undefined, parentElement, inSpan, spanSide];
}

export function findQueryPartByPath(query:Query | QuerySegment, path:string, callback?: Function): [QuerySegment | undefined, any, boolean | undefined, string | undefined] {
  
  const plainQuery:any = query as any,
      topSegment:any = (plainQuery && plainQuery.query) || plainQuery;
  let spanSideVal : spanSide | undefined = undefined;
  let inSpan = false;
  if (path && path.startsWith(".")) {
    path = path.slice(1);
  } if (path && path.endsWith(".left")) {
    path = path.slice(0, -5);
    spanSideVal = spanSide.left;
    inSpan = true;
  } else if (path && path.endsWith(".right")) {
    path = path.slice(0, -6);
    spanSideVal = spanSide.right;
    inSpan = true;
  }
  if (!path) {
    return [topSegment, undefined, inSpan, spanSideVal];
  }
  return findQuerySegmentByPath(topSegment, path, undefined, undefined, callback, inSpan, spanSideVal);
}

export function getSegmentAsCorrectType(segment: any): QueryBranch | QuerySpan | QueryLeafNode | undefined {
  if (!segment) {
    return undefined;
  }
  if (segmentIsBranch(segment)) {
    return segment as QueryBranch;
  } else if (segment?.op === queryOps.span) {
    return segment as QuerySpan;
  }
  // If not a branch or span, it's a leaf node
  return segment as QueryLeafNode;
}

export function getDefaultQuerySegmentsByOp(op: string): QueryBranch | QuerySpan | QueryLeafNode | undefined {
  if ( op === "span" ) {
    return { op: "span", left: undefined, right: undefined } as QuerySpan;
  }
  return getSegmentAsCorrectType({ op: op } as QuerySegment);
}

export function parseQueryFromString(queryString: string): Query {
  let newQuery:Query = new Query({});

  queryString = queryString.trim();
  if (queryString.startsWith("{")) {
    newQuery = new Query(JSON.parse(queryString));
  }

  return newQuery;
}

export function queryOpIsAllowedInSpan(op: queryOps): boolean {
  const spanAllowedOps = [queryOps.equals, queryOps.contains, queryOps.span, queryOps.boolean, queryOps.and, queryOps.or, queryOps.not];
  return spanAllowedOps.includes(op);
}

export function queryOpIsCompare(op: queryOps): boolean {
  return (op === queryOps.lessThan || op === queryOps.lessThanOrEqual ||
    op === queryOps.greaterThan || op === queryOps.greaterThanOrEqual);
}

export function queryOpIsLeafNode(op: queryOps): boolean {
  return queryOpIsCompare(op) ||
    op === queryOps.equals || op === queryOps.contains ||
    op === queryOps.boolean || op === queryOps.range;
}

export function queryOpIsBranch(op: queryOps): boolean {
  return (op === queryOps.and || op === queryOps.or || op === queryOps.not);
}

export function segmentIsBranch(segment: any): boolean {
  if (!segment || !segment?.op) {
    return false;
  }
  return queryOpIsBranch(segment.op);
}

export function segmentIsLeafNode(segment: any): boolean {
  if (!segment || !segment?.op) {
    return false;
  }
  return queryOpIsLeafNode(segment.op);
}

export function segmentIsCompare(segment: any): boolean {
  if (!segment || !segment?.op) {
    return false;
  }
  return queryOpIsCompare(segment.op);
}

export function loadSegment(segment: any) {
  if (typeof segment === "string" || typeof segment !== "object") {
    return segment;
  }

  if (segmentIsBranch(segment)) {
    return new QueryBranch(segment);
  } else if (segment.op === queryOps.span) {
    return new QuerySpan(segment);
  } else {
    return new QueryLeafNode(segment);
  }
}

export const ensureQueryHasRootContainer = (query: Query): Query => {
  // If the query has no content, create a new root branch
  if (!query.query) {
    query.query = new QueryBranch({op: queryOps.and});
  }
  // If the query is only a leaf node, wrap it in a branch
  // This is so the user has a place to add additional query parts in the UI
  if (!queryOpIsBranch(query.query.op)) {
    query.query = new QueryBranch({op: queryOps.and, values: [query.query]});
  }
  return query;
}

export const optimizeQuery = async (query: Query): Promise<Query> => {
  const queryOptimizerResponse = await APIRequest("query/test", {}, {
    method: "POST",
    "body": JSON.stringify(query, undefined , 2)
  }, false, 3, [400]);

  if (queryOptimizerResponse.query) {
    ensureQueryHasRootContainer(queryOptimizerResponse);
    return queryOptimizerResponse;
  }
}

export const findFirstNodeWithField = (query: Query | QuerySegment | QueryBranch | QueryLeafNode | QuerySpan, field: string): QuerySegment | undefined => {
  if (query.query) {
    return findFirstNodeWithField(query.query, field);
  }
  if (segmentIsLeafNode(query) && query?.field === field || query?.fields?.includes(field)) {
    return query;
  }
  if (segmentIsBranch(query)) {
    for (const value of query.values) {
      const foundNode = findFirstNodeWithField(value, field);
      if (foundNode) {
        return foundNode;
      }
    }
  }
  if (query.op === queryOps.span) {
    const leftNode = findFirstNodeWithField(query.left, field);
    if (leftNode) {
      return leftNode;
    }
    return findFirstNodeWithField(query.right, field);
  }
}

export const findFirstNodeOfOp = (query: Query | QuerySegment | QueryBranch | QueryLeafNode | QuerySpan, ops: queryOps): QuerySegment | undefined => {
  if (query.query) {
    return findFirstNodeOfOp(query.query, ops);
  }
  if (ops.includes(query.op)) {
    return query;
  }
  if (segmentIsBranch(query)) {
    for (const value of query.values) {
      const foundNode = findFirstNodeOfOp(value, ops);
      if (foundNode) {
        return foundNode;
      }
    }
  }
  if (query.op === queryOps.span) {
    const leftNode = findFirstNodeOfOp(query.left, ops);
    if (leftNode) {
      return leftNode;
    }
    return findFirstNodeOfOp(query.right, ops);
  }
}

export const optimizeQueries = async (queries: Query[], preserveBoolean?: boolean): Promise<Query[]> => {
  const queryOptimizerPromises = queries.map(quickSearchQuery => {
    return APIRequest("query/test", {}, {
      method: "POST",
      "body": JSON.stringify(quickSearchQuery, undefined , 2)
    }, false, 3, [400]);
  });

  // Wait for all the API requests to finish
  let queryAPIResponses = await Promise.all(queryOptimizerPromises);

  const anyFailed = queryAPIResponses.some(response => !response.query);

  if (!anyFailed) {
    queryAPIResponses = queryAPIResponses.map((response, index) => {
      let query = response as Query

      // If boolean queries should be preserved, keep them as-is
      if (preserveBoolean) {
        query = queries[index] as Query;
      }
      ensureQueryHasRootContainer(query);
      return query as Query;
    });
    return queryAPIResponses;
  }
}

export function enforceBranch(querySegment: QuerySegment, op?: queryOps): QueryBranch {
  const opToAdd = op || queryOps.and;
  if (segmentIsBranch(querySegment)) {
    return querySegment as QueryBranch;
  }
  return new QueryBranch({op: opToAdd, values: [querySegment]});
}
